import { useReducer, useEffect } from 'react'
import api, { IScenarioGroup, IAssignmentPlan } from '@api'

interface State {
  loading: boolean
  groups: IScenarioGroup[]
  parimaryPlan: IAssignmentPlan | null
}

interface ScenarioGroupsHook extends State {
  deleteGroup: (id: string) => void
  deletePlan: (id: string) => void
  updatePlan: (plan: IAssignmentPlan) => void
}

enum Actions {
  LIST,
  PLAN_DELETE,
  PLAN_UPDATE,
  GROUP_DELETE,
}

interface List {
  type: Actions.LIST
  groups: IScenarioGroup[]
}

interface UpdatePlan {
  type: Actions.PLAN_UPDATE
  plan: IAssignmentPlan
}

interface DeletePlan {
  type: Actions.PLAN_DELETE
  id: string
}
interface DeleteGroup {
  type: Actions.GROUP_DELETE
  id: string
}

type RootAction = List | DeletePlan | UpdatePlan | DeleteGroup

let persistedState: State = {
  loading: true,
  groups: [],
  parimaryPlan: null,
}

export default function useScenarioGroups(): ScenarioGroupsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)

  useEffect(() => {
    api.ap.list().then((groups) => {
      dispatch({ type: Actions.LIST, groups })
    })
  }, [])

  useEffect(() => {
    persistedState = state
  })

  return {
    ...state,
    deleteGroup: (id) => dispatch({ type: Actions.GROUP_DELETE, id }),
    deletePlan: (id) => dispatch({ type: Actions.PLAN_DELETE, id }),
    updatePlan: (plan: IAssignmentPlan) => dispatch({ type: Actions.PLAN_UPDATE, plan }),
  }
}

function reducer(state: State, action: RootAction) {
  switch (action.type) {
    case Actions.LIST:
      return {
        ...state,
        loading: false,
        groups: action.groups,
      }

    case Actions.PLAN_UPDATE:
      return {
        ...state,
        groups: state.groups.map((group) => {
          const assignmentPlans = group.assignmentPlans.map((plan) =>
            plan.id === action.plan.id ? action.plan : plan
          )
          return { ...group, assignmentPlans }
        }),
      }

    case Actions.PLAN_DELETE:
      return {
        ...state,
        groups: state.groups.reduce((result, group) => {
          const assignmentPlans = group.assignmentPlans.filter((plan) => plan.id !== action.id)
          // Set apType to master if only one plan in the group left
          if (assignmentPlans.length === 1) {
            assignmentPlans[0].apType = 'master'
          }
          return assignmentPlans.length ? [...result, { ...group, assignmentPlans }] : result
        }, [] as IScenarioGroup[]),
      }

    case Actions.GROUP_DELETE:
      return {
        ...state,
        groups: state.groups.filter(({ id }) => id !== action.id),
      }
  }
}
